import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { McDashboardService } from 'src/app/api/mc-dashboard.service';
import { ChartData, ChartOptions } from 'chart.js';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';

interface DimensionHeader {
  name: string;
}

interface MetricHeader {
  name: string;
  type: string;
}

interface DimensionValue {
  value: string;
  oneValue: string;
}

interface MetricValue {
  value: string;
  oneValue: string;
}

interface GoogleAnalyticsRow {
  dimensionValues: DimensionValue[];
  metricValues: MetricValue[];
}

interface GoogleAnalyticsMetadata {
  dataLossFromOtherRow: boolean;
  currencyCode: string;
  _currencyCode: string;
  timeZone: string;
  _timeZone: string;
}

interface GoogleAnalyticsResponse {
  dimensionHeaders: DimensionHeader[];
  metricHeaders: MetricHeader[];
  rows: GoogleAnalyticsRow[];
  totals: GoogleAnalyticsRow[];
  maximums: any[];
  minimums: any[];
  rowCount: number;
  metadata: GoogleAnalyticsMetadata;
  propertyQuota: any;
  kind: string;
}

@Component({
  selector: 'app-google-analytics-graph',
  templateUrl: './google-analytics-graph.component.html',
  styleUrls: ['./google-analytics-graph.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    DecimalPipe,
    BaseChartDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleAnalyticsGraphComponent implements OnInit, OnDestroy {
  @Input() chartId!: string;
  @Input() currencyFormat: boolean = false;
  @Input() beginAtZero: boolean = true;
  @Input() stepSize: number = 1;
  @Input() backgroundColor: string = 'rgba(75, 192, 192, 0.1)';
  @Input() foregroundColor: string = 'rgba(75, 192, 192, 1)';

  private ngUnsubscribe = new Subject<void>();
  loading = signal(true);
  error = signal<string | null>(null);
  thisYearTotal = signal(0);
  lastYearTotal = signal(0);
  private rawData = signal<{ thisYear: GoogleAnalyticsResponse[], lastYear: GoogleAnalyticsResponse[] } | null>(null);

  private dollarFormat = new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  private dollarFormatNoDecimal = new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    hover: {
      mode: 'x'
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.raw as number;
            if (this.currencyFormat) {
              return context.dataset.label + ': ' + this.dollarFormat.format(value);
            } else {
              return context.dataset.label + ': ' + value.toLocaleString();
            }
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: this.beginAtZero,
        ticks: {
          stepSize: this.stepSize,
          callback: (tickValue: string | number) => {
            const value = Number(tickValue);
            if (this.currencyFormat) {
              return this.dollarFormatNoDecimal.format(value);
            } else {
              return value.toLocaleString();
            }
          }
        }
      }
    }
  };

  chartData = computed<ChartData<'line'>>(() => {
    const data = this.rawData();
    if (!data) return { labels: [], datasets: [] };

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const labels: string[] = [];
    
    // Process this year's data
    const data1 = data.thisYear[0].rows.map(row => Number(row.metricValues[0].value));
    const data2 = data.lastYear[0].rows.map(row => Number(row.metricValues[0].value));

    // Generate labels from this year's data
    data.thisYear[0].rows.forEach(row => {
      labels.push(months[Number(row.dimensionValues[0].value) - 1]);
    });

    // Ensure arrays are the same length and handle undefined values
    for (let i = 0; i < labels.length; i++) {
      if (data1[i] === undefined) data1[i] = 0;
      if (data2[i] === undefined) data2[i] = 0;
    }

    return {
      labels,
      datasets: [
        {
          label: 'This Year',
          backgroundColor: this.backgroundColor,
          borderColor: this.foregroundColor,
          pointBorderColor: "#fff",
          pointBackgroundColor: this.foregroundColor,
          pointHoverBackgroundColor: this.foregroundColor,
          pointHoverBorderColor: this.foregroundColor,
          data: data1,
          tension: 0.4,
          fill: true
        },
        {
          label: 'Last Year',
          pointBackgroundColor: "rgba(150,150,150,.8)",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "rgba(150,150,150,.8)",
          backgroundColor: "rgba(150,150,150,0.1)",
          borderColor: "rgba(150,150,150,.8)",
          data: data2,
          tension: 0.4,
          fill: true
        }
      ]
    } as ChartData<'line'>;
  });

  constructor(private dashboardService: McDashboardService) {}

  ngOnInit() {
    this.loadChartData();
  }

  private loadChartData() {
    this.loading.set(true);
    this.error.set(null);

    // Get the last day of the current month
    const today = new Date();
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const yearAgoLastDay = new Date(lastDayOfMonth.getFullYear() - 1, lastDayOfMonth.getMonth(), lastDayOfMonth.getDate());
    const twoYearsAgoLastDay = new Date(lastDayOfMonth.getFullYear() - 2, lastDayOfMonth.getMonth(), lastDayOfMonth.getDate());

    this.dashboardService.getGoogleAnalyticsChartData(this.chartId, yearAgoLastDay, lastDayOfMonth)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (thisYearResponse: GoogleAnalyticsResponse[]) => {
          this.dashboardService.getGoogleAnalyticsChartData(this.chartId, twoYearsAgoLastDay, yearAgoLastDay)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
              next: (lastYearResponse: GoogleAnalyticsResponse[]) => {
                if (!thisYearResponse || !lastYearResponse) {
                  throw new Error('No data received from server');
                }

                // Set totals from the totals array
                this.thisYearTotal.set(Number(thisYearResponse[0].totals[0]?.metricValues[0]?.value || 0));
                this.lastYearTotal.set(Number(lastYearResponse[0].totals[0]?.metricValues[0]?.value || 0));

                // Store the raw data
                this.rawData.set({
                  thisYear: thisYearResponse,
                  lastYear: lastYearResponse
                });

                this.loading.set(false);
              },
              error: (err: Error) => {
                this.error.set('Failed to load last year data: ' + (err instanceof Error ? err.message : 'Unknown error'));
                this.loading.set(false);
                console.error('Error loading last year data:', err);
              }
            });
        },
        error: (err: Error) => {
          this.error.set('Failed to load this year data: ' + (err instanceof Error ? err.message : 'Unknown error'));
          this.loading.set(false);
          console.error('Error loading this year data:', err);
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 